import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function Signup() {
  const { signup, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
  const [err, setErr] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await signup(form);
      toast.success("Workspace ready — 100 tokens credited");
      nav("/app");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Signup failed";
      setErr(msg); toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-sky-500 rounded-lg flex items-center justify-center shadow-sm">
              <span className="font-display font-black text-white text-lg">A</span>
            </div>
            <div>
              <div className="font-display font-bold text-[15px] text-slate-900">AIMOS</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">by Neuronex.ai</div>
            </div>
          </Link>
          <div>
            <h1 className="font-display font-bold text-3xl text-slate-900">Start free.</h1>
            <p className="text-sm text-slate-500 mt-1">100 tokens on us — enough for 2 reels or 1 film.</p>
          </div>
          <div className="space-y-3.5">
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Your name</Label>
              <Input data-testid="signup-name" value={form.name} onChange={upd("name")} placeholder="Your full name" className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11" required />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Brand / organization</Label>
              <Input data-testid="signup-org" value={form.organization} onChange={upd("organization")} placeholder="Kazo, Bata, or your agency" className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11" required />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Work email</Label>
              <Input data-testid="signup-email" type="email" value={form.email} onChange={upd("email")} placeholder="you@brand.com" className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11" required />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Password</Label>
              <Input data-testid="signup-password" type="password" value={form.password} onChange={upd("password")} placeholder="8+ characters" className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md h-11" required minLength={8} />
            </div>
            {err && <div className="text-xs text-red-600 font-mono">{err}</div>}
            <Button data-testid="signup-submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11 rounded-md shadow-sm">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create workspace"}
            </Button>
          </div>
          <div className="text-sm text-slate-600">
            Already have a workspace?{" "}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
          </div>
        </form>
      </div>
      <div className="hidden lg:flex w-1/2 relative bg-gradient-to-br from-blue-50 via-sky-50 to-white border-l border-slate-100 items-center justify-center p-12">
        <div className="max-w-md space-y-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-blue-600">// What's included free</div>
          <Bullet>100 tokens — 2 reels or 1 two-minute film</Bullet>
          <Bullet>24 pre-built diverse AI influencers</Bullet>
          <Bullet>Custom influencer builder — Nano Banana × 4 variants per brief</Bullet>
          <Bullet>URL → Reel: paste any product page, get a character-driven ad</Bullet>
          <Bullet>Hindi + 8 languages with burned-in English subtitles</Bullet>
          <Bullet>Public share pages + one-click download</Bullet>
          <div className="text-xs text-slate-500 pt-4 border-t border-slate-200">
            Cancel anytime. No credit card required to start. Top up via Razorpay when you're ready to scale.
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}
